import { Sparkles, Star } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface PresetRowProps {
  presetName: string;
  presetId: string;
  toolName: string;
  toolDescription: string;
  selected: boolean;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isRecommended: boolean;
  recommendedReason?: string;
  onSelect: () => void;
  onAddToPipeline: () => void;
}

export const PresetRow: React.FC<PresetRowProps> = ({
  presetName,
  toolName,
  toolDescription,
  selected,
  isFavorite,
  onToggleFavorite,
  isRecommended,
  recommendedReason,
  onSelect,
  onAddToPipeline,
}) => {
  const { t } = useTranslation('sidebar');
  return (
    <div
      className={
        'group flex items-center gap-2 rounded-md px-2 py-2 border border-transparent ' +
        (selected
          ? 'bg-accent text-accent-foreground'
          : isRecommended
            ? 'bg-primary/10 border border-primary/30 hover:bg-primary/20'
            : 'hover:bg-accent/60')
      }
    >
      <button
        onClick={onSelect}
        className="flex-1 min-w-0 text-left"
        title={isRecommended ? recommendedReason : toolDescription}
      >
        <div className="text-sm font-medium truncate flex items-center gap-1">
          {presetName}
          {isRecommended && <Sparkles className="h-3.5 w-3.5 text-primary" />}
        </div>
        <div className="text-xs text-muted-foreground truncate">{toolName}</div>
      </button>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 px-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite();
              }}
              aria-label={isFavorite ? t('tools.unfavorite') : t('tools.favorite')}
            >
              <Star
                className={
                  'h-4 w-4 ' + (isFavorite ? 'text-primary fill-current' : 'text-muted-foreground')
                }
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{isFavorite ? t('tools.unfavorite') : t('tools.favorite')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={selected ? 'ghost' : 'secondary'}
              size="sm"
              className="h-7 px-2"
              onClick={onAddToPipeline}
              aria-label={t('tools.addToPipeline')}
            >
              +
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('tools.addToPipeline')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
